import { create } from 'zustand';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../lib/msal-config';

// Check if we're in dev mode without Azure AD configured
const isDevModeWithoutAuth = import.meta.env.DEV && !import.meta.env.VITE_AZURE_AD_CLIENT_ID;

// Mock user for dev mode
const mockUser: AccountInfo = {
  homeAccountId: 'dev-user',
  localAccountId: 'dev-user',
  environment: 'dev',
  tenantId: 'dev-tenant',
  username: 'dev@bettermatch.local',
  name: 'Dev User',
};

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  error: string | null;

  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  checkAuth: () => void;
  forceReauthenticate: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,

  checkAuth: () => {
    // Dev mode bypass - auto-authenticate
    if (isDevModeWithoutAuth) {
      set({ isAuthenticated: true, user: mockUser, isLoading: false });
      return;
    }

    // Check if MSAL has any accounts
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    if (activeAccount) {
      set({ isAuthenticated: true, user: activeAccount, isLoading: false });
    } else if (accounts.length > 0) {
      // Set the first account as active if none is set
      msalInstance.setActiveAccount(accounts[0]);
      set({ isAuthenticated: true, user: accounts[0], isLoading: false });
    } else {
      set({ isAuthenticated: false, user: null, isLoading: false });
    }
  },

  login: async () => {
    // Dev mode bypass - auto-authenticate
    if (isDevModeWithoutAuth) {
      set({ isAuthenticated: true, user: mockUser, isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Check if user is already authenticated
      const activeAccount = msalInstance.getActiveAccount();
      if (activeAccount) {
        // Try silent token acquisition to validate the session
        try {
          await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: activeAccount,
            forceRefresh: false, // Let MSAL manage caching and refresh
          });
          set({ isAuthenticated: true, user: activeAccount, isLoading: false });
          return;
        } catch (silentError) {
          // Silent acquisition failed, need interactive login
          console.log('[Auth] Silent token acquisition failed, redirecting to login');
        }
      }

      // Redirect to Microsoft login
      // Note: This will navigate away from the page
      await msalInstance.loginRedirect(loginRequest);
      // Page will redirect, state updates happen via MsalProvider after redirect back
    } catch (error) {
      console.error('[Auth] Login failed:', error);
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
      });
    }
  },

  logout: async () => {
    // Dev mode bypass
    if (isDevModeWithoutAuth) {
      set({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }

    set({ isLoading: true });

    try {
      // Redirect to Microsoft logout
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
      // Page will redirect, state will be reset on reload
    } catch (error) {
      console.error('[Auth] Logout failed:', error);
      // Clear local state even if MSAL logout fails
      set({ isAuthenticated: false, user: null, isLoading: false });
    }
  },

  getAccessToken: async () => {
    // Dev mode bypass - return mock token
    if (isDevModeWithoutAuth) {
      return 'dev-mock-token';
    }

    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) {
      // No active account, check if there are any accounts
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }
      // Set the first account as active
      msalInstance.setActiveAccount(accounts[0]);
    }

    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: false, // Let MSAL handle caching and refresh
      });
      // Use idToken instead of accessToken - the API validates against our Client ID
      // as the audience, which matches the idToken but not the Graph accessToken
      return response.idToken;
    } catch (error) {
      // Check if interaction is required (token expired, consent needed, etc.)
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('[Auth] Token expired, attempting interactive refresh via popup');
        try {
          // Try popup first — keeps the user on the current page
          const response = await msalInstance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          return response.idToken;
        } catch (popupError) {
          console.warn('[Auth] Popup token refresh failed, forcing full re-login');
          await useAuthStore.getState().forceReauthenticate();
          return null;
        }
      }

      console.error('[Auth] Token acquisition failed:', error);
      // Don't automatically logout on other errors
      return null;
    }
  },

  /**
   * Force re-authentication by clearing MSAL cache and redirecting to login.
   * Call this when the API returns 401 (token expired server-side).
   */
  forceReauthenticate: async () => {
    if (isDevModeWithoutAuth) {
      set({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }

    console.log('[Auth] Forcing re-authentication');

    // Clear MSAL account cache
    const accounts = msalInstance.getAllAccounts();
    for (const account of accounts) {
      await msalInstance.clearCache({ account });
    }

    // Clear local state
    set({ isAuthenticated: false, user: null, isLoading: false });

    // Redirect to login
    await msalInstance.loginRedirect(loginRequest);
  },
}));
